import { replace } from 'connected-react-router';
import { EventTypes } from 'redux-segment';
import { RSAA } from 'redux-api-middleware';

import sortOrgsList from 'services/sortOrganizations/index';
import {
  LOG_IN_REQUESTED,
  LOG_OUT_REQUESTED,
  GET_FEATURE_FLAGS_REQUESTED,
  GET_FEATURE_FLAGS_SUCCESS,
  UPDATE_FEATURE_FLAGS_SUCCESS,
  SET_SESSION_SUCCESS,
  SET_SESSION_FAILURE,
  CREATE_EMAIL_VERIFICATION_REQUESTED,
  CREATE_EMAIL_VERIFICATION_SUCCESS,
  CREATE_EMAIL_VERIFICATION_FAILURE,
  IMPERSONATE_ORG,
  STOP_ORG_IMPERSONATION,
  SEARCH_ORGS_REQUESTED,
  SEARCH_ORGS_SUCCESS,
  SEARCH_ORGS_FAILURE,
  SORT_ORGS,
  START_MFA_SIGNUP_FLOW_REQUESTED,
  START_MFA_SIGNUP_FLOW_SUCCESS,
  START_MFA_SIGNUP_FLOW_FAILURE,
  GET_MFA_STATUS_REQUESTED,
  GET_MFA_STATUS_SUCCESS,
  GET_MFA_STATUS_FAILURE,
  REMOVE_MFA_ENROLLMENT_REQUESTED,
  REMOVE_MFA_ENROLLMENT_SUCCESS,
  REMOVE_MFA_ENROLLMENT_FAILURE,
  AWS_SIGNUP_FAILURE,
} from '../actions';
import auth from '../../services/auth';
import featureFlags from '../../services/featureFlags';
import { getIdentityState } from './selectors';
import { setClearBannerState } from '../notifications/actionCreators';

const createEmailVerificationActionSet = (data) => ({
  [RSAA]: {
    types: [
      CREATE_EMAIL_VERIFICATION_REQUESTED,
      CREATE_EMAIL_VERIFICATION_SUCCESS,
      CREATE_EMAIL_VERIFICATION_FAILURE,
    ],
    method: 'POST',
    endpoint: '/emailverification/',
    body: JSON.stringify(data),
  },
});

export const sendEmailVerification = (details) => (dispatch) =>
  dispatch(createEmailVerificationActionSet(details));

/**
 * Initiates a silent login procedure in a hidden iframe. If the user is already logged in, this
 * will update the user's tokens without prompting them to do so. Can also send them to a new
 * destination once the call to update the session has been intiated.
 */
export const updateSession = (destination) => (dispatch) =>
  auth.checkSession().then(
    (authResult) => {
      const payload = authResult.idTokenPayload[process.env.REACT_APP_AUTH0_AUDIENCE];
      const analyticsPayload = {
        id: authResult.idTokenPayload.sub,
        traits: {
          email: payload.email,
        },
      };
      dispatch({
        type: SET_SESSION_SUCCESS,
        payload: authResult,
        meta: {
          analytics: {
            eventType: EventTypes.identify,
            eventPayload: analyticsPayload,
          },
        },
      });
      if (destination) {
        dispatch(replace(destination));
      }
    },
    (error) => {
      dispatch({ type: SET_SESSION_FAILURE, error });
      dispatch(replace('/logout'));
    },
  );

/**
 * Initiates a login that will redirect the user to the desired originatingURL
 * upon logging in.
 *
 * @param  {string} originatingURL - The URL be visited when login was intitiated
 */
export const logIn = (originatingURL) => (dispatch) => {
  dispatch({
    type: LOG_IN_REQUESTED,
    payload: originatingURL,
  });
  auth.manualAuthorize();
};

export const logInSSO = (originatingURL) => (dispatch) => {
  dispatch({
    type: LOG_IN_REQUESTED,
    payload: originatingURL,
  });
  auth.manualAuthorize(true);
};

/**
 * Initiate a sign up process for an AWS customer.
 * If usccessful, also logs them in and redirects to the AWS onboarding page
 */
export const logInAWS = (data, awsToken) => (dispatch) =>
  auth.logInAWS(data, awsToken).catch((err) => {
    const sanitizedError = err;
    if (err && err.original) {
      delete sanitizedError.original;
    }
    dispatch({
      type: AWS_SIGNUP_FAILURE,
      payload: err,
    });
    return err;
  });

/**
 * Initiate a log out
 */
export const logOut = () => (dispatch) => {
  dispatch({
    type: LOG_OUT_REQUESTED,
    meta: {
      analytics: true,
    },
  });
  auth.logOut();
};

/**
 * Set session based on token passed in URL
 *
 * @param {string} location - The current URL, including the token
 * @param {string} destination - The URL to direct to when session is set
 */
export const setSession = (location, destination) => (dispatch) =>
  auth.parseCreds(location).then(
    (creds) => {
      const payload = creds.idTokenPayload[process.env.REACT_APP_AUTH0_AUDIENCE];
      const analyticsPayload = {
        userId: creds.idTokenPayload.sub,
        traits: {
          email: payload.email,
        },
      };
      dispatch({
        type: SET_SESSION_SUCCESS,
        payload: creds,
        meta: {
          analytics: {
            eventType: EventTypes.identify,
            eventPayload: analyticsPayload,
          },
        },
      });
      dispatch(replace(destination || '/'));
    },
    (error) => {
      dispatch({
        type: SET_SESSION_FAILURE,
        error,
      });
      dispatch(replace('/error/400'));
    },
  );

/**
 * Subscribe to streaming updates to the feature flags for same user that
 * initialized the loading of the feature flags.
 */
export const subscribeToFeatureFlags = () => (dispatch) => {
  featureFlags.subscribe((flags) => {
    dispatch({
      type: UPDATE_FEATURE_FLAGS_SUCCESS,
      payload: flags,
    });
  });
};

/**
 * Unsubscribe to streaming updates to feature flags for same user that
 * initialized the loading of the feature flags.
 */
export const unsubscribeFromFeatureFlags = () => () => {
  featureFlags.unsubscribe();
};

/**
 * Get feature flags for the given user
 *
 * @param {object} user - The user to get the feature flags for
 * @returns {Promise} - Promise that will be resolved when feature flags load
 */
export const getFeatureFlags = (user = {}) => (dispatch) => {
  dispatch({
    type: GET_FEATURE_FLAGS_REQUESTED,
  });
  return featureFlags.getFlags(user, user.featureFlags).then(
    (val) => {
      dispatch({
        type: GET_FEATURE_FLAGS_SUCCESS,
        payload: val,
      });
      subscribeToFeatureFlags()(dispatch);
    },
    () => {
      dispatch({
        type: GET_FEATURE_FLAGS_SUCCESS,
        payload: {},
      });
    },
  );
};

const impersonateOrgActionSet = (id) => ({
  type: IMPERSONATE_ORG,
  payload: id,
});

/**
 * Impersonatate an org
 *
 * @param {string} orgId - The id of the organization a user will impersonate
 */
export const impersonateOrg = (orgId) => (dispatch) => {
  dispatch(impersonateOrgActionSet(orgId));
  dispatch(setClearBannerState());
};

const stopOrgImpersonationActionSet = () => ({
  type: STOP_ORG_IMPERSONATION,
  payload: null,
});

/**
 * Stop org impersonation
 */
export const stopOrgImpersonation = () => (dispatch) => {
  dispatch(stopOrgImpersonationActionSet());
  dispatch(setClearBannerState());
};

/**
 * Search for an Org to Impersonate
 *
 * @param {string} query - The search parameters used to find an organization to impersonate
 */
const searchOrgsActionSet = (query) => ({
  [RSAA]: {
    types: [
      {
        type: SEARCH_ORGS_REQUESTED,
        payload: query,
      },
      SEARCH_ORGS_SUCCESS,
      SEARCH_ORGS_FAILURE,
    ],
    method: 'GET',
    endpoint: `/organizations/?search=${query}`,
  },
});

export const searchOrgs = (query) => (dispatch) => dispatch(searchOrgsActionSet(query));

/**
 * Sort the list of Orgs after searching for them
 *
 * @param {string} sortBy - The value of the sortBy select field on the orgImpersonationPage
 */
const sortOrgsActionSet = (sortBy, list) => ({
  type: SORT_ORGS,
  payload: {
    sortBy,
    list,
  },
});

export const sortOrgs = (sortBy) => (dispatch, getState) => {
  const { list } = getIdentityState(getState()).orgSearchList;
  if (!list.length) return dispatch(sortOrgsActionSet(sortBy, null));
  return dispatch(sortOrgsActionSet(sortBy, sortOrgsList(sortBy, list)));
};

/**
 * Start the MFA signup flow for the user
 */
const startMFASignupFlowActionSet = (userId, sendMail) => ({
  [RSAA]: {
    types: [
      START_MFA_SIGNUP_FLOW_REQUESTED,
      START_MFA_SIGNUP_FLOW_SUCCESS,
      START_MFA_SIGNUP_FLOW_FAILURE,
    ],
    method: 'POST',
    endpoint: '/mfa/',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      send_mail: sendMail,
    }),
  },
});

export const startMFASignupFlow = (userId, sendMail) => (dispatch) =>
  // Do not use with dev+ricketyclickit
  dispatch(startMFASignupFlowActionSet(userId, sendMail)).then((response) => {
    if (response.type === START_MFA_SIGNUP_FLOW_SUCCESS) {
      dispatch(replace('/logout'));
    }
  });

/**
 * Get the MFA status for the user
 */
const getMFAStatusActionSet = (userId) => ({
  [RSAA]: {
    types: [GET_MFA_STATUS_REQUESTED, GET_MFA_STATUS_SUCCESS, GET_MFA_STATUS_FAILURE],
    method: 'GET',
    endpoint: `/mfa/${userId}/enrollments/`,
    headers: { 'Content-Type': 'application/json' },
  },
});

export const getMFAStatus = (userId) => (dispatch) => {
  dispatch(getMFAStatusActionSet(userId));
};

/**
 * Remove MFA Enrollment for the user
 */
const removeMFAEnrollmentActionSet = (userId, enrollmentId) => ({
  [RSAA]: {
    types: [
      REMOVE_MFA_ENROLLMENT_REQUESTED,
      REMOVE_MFA_ENROLLMENT_SUCCESS,
      REMOVE_MFA_ENROLLMENT_FAILURE,
    ],
    method: 'DELETE',
    endpoint: `/mfa/${userId}/enrollments/${enrollmentId}/`,
    headers: { 'Content-Type': 'text/html; charset=UTF-8' },
  },
});

export const removeMFAEnrollment = (userId, enrollmentId) => (dispatch) =>
  dispatch(removeMFAEnrollmentActionSet(userId, enrollmentId));
